import { useEffect, useSyncExternalStore } from 'react';
import { AppState, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import { GetMissionDetailsDto, GetMissionReferralResponse, GetMissionsFromMapDto, MissionReferralStatus, MissionsClient } from '../api/api.g';
import { createMissionReferralRegistry, referralStorageKey } from '../helpers/missionReferralRegistry';
import { LoggedState } from '../models/AuthModelsCommon';
import { missionsActions } from '../store/missionsSlice';
import { useAppDispatch, useAppSelector } from './store-hook';
import useApi from './api-hook';
import { readMissions } from '../helpers/missionRead';
import { promotionRefreshSeconds } from '../helpers/missionPromotions';

export const missionReferralRegistry = createMissionReferralRegistry(AsyncStorage);
let refreshRegistry: (() => void) | undefined;
export const refreshMissionReferrals = () => refreshRegistry?.();

export function referralListing(detail: GetMissionDetailsDto, referral: GetMissionReferralResponse, canAccept = false) {
  return GetMissionsFromMapDto.fromJS({
    ...detail,
    branchId: referral.branchId,
    periodMissionConfigurationIds: [referral.periodMissionConfigurationId],
    missionConfigurationNames: [detail.missionConfigurationName],
    listingKey: `referral:${referral.branchId}:${referral.periodMissionConfigurationId}`,
    canAccept,
    isAvailableForDiscovery: true,
  }).toJSON();
}

export default function useMissionReferrals() {
  const auth = useAppSelector(state => state.auth);
  const key = auth.logged === LoggedState.Logged && auth.user?.uid && !auth.user.isAnonymous
    ? referralStorageKey(Constants.expoConfig?.extra?.baseUrl ?? '', auth.user.uid) : undefined;
  const records = useSyncExternalStore(missionReferralRegistry.subscribe,
    () => missionReferralRegistry.snapshot(key), () => missionReferralRegistry.snapshot(key));
  return { key, records };
}

/** Mounted once by MainBody on both platforms. Does not open UI or accept missions. */
export function useMissionReferralRegistryLifecycle() {
  const { key, records } = useMissionReferrals();
  const token = useAppSelector(state => state.auth.idToken);
  const seed = useAppSelector(state => state.missions.missionListSeed);
  const language = useAppSelector(state => state.userPrefs.uiLanguage);
  const dispatch = useAppDispatch();
  const api = useApi(MissionsClient);

  useEffect(() => {
    dispatch(missionsActions.setMissionReferralMapTarget(undefined));
    void missionReferralRegistry.activate(key);
  }, [key, dispatch]);

  useEffect(() => {
    if (!key || !token) return;
    let disposed = false;
    const cancellation = new AbortController();
    let running = false;
    let rerun = false;
    const refresh = async () => {
      if (running) { rerun = true; return; }
      running = true;
      try {
        await missionReferralRegistry.activate(key);
        await missionReferralRegistry.prune(key);
        for (const record of missionReferralRegistry.snapshot(key)) {
          if (disposed) break;
          let invitationChecked = false;
          try {
            const referral = await readMissions(cancelToken => api.getMissionReferral(record.token, cancelToken), cancellation.signal);
            invitationChecked = true;
            if (disposed) break;
            if (!referral.canAccept || referral.isExpired || referral.status !== MissionReferralStatus.InvitationSent) {
              await missionReferralRegistry.remove(key, record.token);
              continue;
            }
            const details = await readMissions(cancelToken => api.getMissionDetails(referral.branchId, undefined, false, undefined, [referral.periodMissionConfigurationId], cancelToken), cancellation.signal);
            const eligibility = await readMissions(cancelToken => api.getMissionReferralAcceptability(record.token, cancelToken), cancellation.signal);
            if (disposed) break;
            const detail = details.missionDetails?.find(item => item.periodMissionConfigurationId === referral.periodMissionConfigurationId);
            await missionReferralRegistry.update(key, record.token, {
              expiresAt: referral.expiresAt.toISOString(),
              promotionDataUpdatedAt: detail ? Date.now() : record.promotionDataUpdatedAt,
              listing: detail ? referralListing(detail, referral, eligibility.canAccept) : record.listing,
            });
          } catch (error: any) {
            // Only a definitive missing/gone invitation is discarded on HTTP failure.
            if (!disposed && !invitationChecked && [404, 410].includes(error?.status ?? error?.response?.status)) {
              await missionReferralRegistry.remove(key, record.token);
            }
          }
        }
      } finally {
        running = false;
        if (rerun && !disposed) { rerun = false; void refresh(); }
      }
    };
    void refresh();
    refreshRegistry = () => { void refresh(); };
    const subscription = AppState.addEventListener('change', state => { if (state === 'active') void refresh(); });
    // Handles web tab return as well as native foreground, and local expiry while open.
    const onFocus = () => { void refresh(); };
    const browserFocusTarget = Platform.OS === 'web' && typeof window !== 'undefined' ? window : undefined;
    browserFocusTarget?.addEventListener('focus', onFocus);
    const expiryTimer = setInterval(() => { void missionReferralRegistry.prune(key); }, 30_000);
    return () => {
      disposed = true;
      cancellation.abort();
      refreshRegistry = undefined;
      subscription.remove();
      clearInterval(expiryTimer);
      browserFocusTarget?.removeEventListener('focus', onFocus);
    };
  }, [key, token, seed, language, api]);

  useEffect(() => {
    const boundaries = records.map(record => {
      const seconds = promotionRefreshSeconds(record.listing);
      return seconds === undefined ? Infinity : (record.promotionDataUpdatedAt ?? record.openedAt) + seconds * 1000 + 250;
    });
    const next = Math.min(...boundaries);
    if (!key || !token || !Number.isFinite(next)) return;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = () => {
      if (Date.now() >= next) refreshMissionReferrals();
      // A failed revalidation keeps the invitation and retries the overdue promotion check.
      timer = setTimeout(refresh, Date.now() >= next ? 60_000 : Math.min(next - Date.now(), 86_400_000));
    };
    timer = setTimeout(refresh, Math.max(1, Math.min(next - Date.now(), 86_400_000)));
    return () => clearTimeout(timer);
  }, [key, token, records]);
}
